import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NewDashboardOrgType } from 'helpers/repTypeHelper';

type NewsFeedItem = {
  title: string;
  publishAt: string;
  url: string;
  author: string;
  thumbnail_url: string;
  snippet: string;
  img: {
    landscape: string;
    portrait: string;
    square: string;
  };
  coverImgPublicUrl: string;
  site: {
    siteName: string;
  };
};

// This mapping is specific to the news feed API in ai-sales-dashboard-backend
const NEWS_FEED_ORG_TYPE_MAP = {
  BLOCK: 'STREET',
  STREET: 'STREET',
  CHAIN: 'FIELD',
  AIR: 'AIR',
  UNKNOWN: 'UNKNOWN',
};

const useNewsFeed = (
  orgType: NewDashboardOrgType,
): {
  newsFeed: NewsFeedItem[];
  isNewsFeedLoading: boolean;
  newsFeedError: string | null;
  isNewsFeedLoadedEmpty: boolean;
} => {
  const [newsFeed, setNewsFeed] = useState<NewsFeedItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newsFeedError, setNewsFeedError] = useState<string | null>(null);
  const [isNewsFeedLoadedEmpty, setIsNewsFeedLoadedEmpty] = useState(false);
  const [searchParams] = useSearchParams();
  const jwt = searchParams.get('jwt');
  const emailFromParams = searchParams.get('email');

  useEffect(() => {
    if (!jwt || !orgType || isLoading || newsFeed.length > 0) {
      return;
    }

    const salesOrg = NEWS_FEED_ORG_TYPE_MAP[orgType];

    const params = new URLSearchParams({
      jwt,
      sales_org: salesOrg,
      campaign: salesOrg,
      email: emailFromParams || '',
    });
    const apiUrl = `https://sales.dashboard.ai.sunrun.com/api/shared/news_feed?${params.toString()}`;

    setIsLoading(true);
    setNewsFeedError(null);
    setIsNewsFeedLoadedEmpty(false);
    fetch(apiUrl, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(async (response) => {
        const data = await response.json();
        const items = data.body?.news_items || [];
        setNewsFeed(items);
        setIsNewsFeedLoadedEmpty(items.length === 0);
      })
      .catch(() => {
        setNewsFeedError('Unable to load news. Please try again.');
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ignoring emailFromParams since it comes from same place as jwt
  }, [jwt, orgType]);

  return {
    newsFeed,
    isNewsFeedLoading: isLoading,
    newsFeedError,
    isNewsFeedLoadedEmpty,
  };
};

export { useNewsFeed };
export type { NewsFeedItem };
